// Taps the widget's WebSocket for the events it does not expose.
//
// Dograh pushes a stream of rtf-* messages down the same socket the widget uses
// for signalling. The widget understands a handful and logs the rest as
// "Unknown message type" - and the unknown ones are the useful ones:
//
//   rtf-bot-started-speaking / rtf-bot-stopped-speaking
//       exact speaking state, better than inferring it from output amplitude
//   rtf-user-transcription / rtf-bot-text
//       both sides of the transcript, which the public API does not expose
//       for voice widgets at all
//   rtf-node-transition
//       which workflow node Misti moved to, so a surprise ending is visible
//   rtf-ttfb-metric / rtf-latency-measured
//       real per-turn latency instead of guesswork
//
// Observed message shape:
//   { type, payload: {...}, node_id, node_name }
// The text lives inside payload, and the payload key names are undocumented,
// so textOf() tries the plausible ones and logs the actual keys once per type.

let tapped = null;
let pollTimer = null;

const noop = () => {};

// Types the widget itself does not handle. We swallow these rather than forward
// them, purely to stop it logging "Unknown message type" for every single one.
// Anything not listed here is passed through untouched.
const WIDGET_IGNORES = new Set([
	"rtf-bot-started-speaking",
	"rtf-bot-stopped-speaking",
	"rtf-bot-text",
	"rtf-user-transcription",
	"rtf-node-transition",
	"rtf-ttfb-metric",
	"rtf-latency-measured",
	"rtf-function-call-start",
	"rtf-function-call-end",
	"rtf-user-mute-started",
	"rtf-user-mute-stopped",
]);

// Observed shapes: rtf-bot-text {text}, rtf-user-transcription {text, final,
// timestamp, user_id}. The fallbacks remain in case other events differ.
const TEXT_KEYS = ["text", "content", "transcript", "message", "body"];

function textOf(msg) {
	const p = msg.payload || msg;
	for (const k of TEXT_KEYS) {
		if (typeof p[k] === "string" && p[k].trim()) return p[k];
	}
	// Fall back to the first non-trivial string in the payload.
	for (const v of Object.values(p)) {
		if (typeof v === "string" && v.trim().length > 1) return v;
	}
	return "";
}

// Dograh reports timings in SECONDS, under keys like ttfb_seconds and
// latency_seconds. Returned in milliseconds for consistency with everything
// else in the app.
function millisOf(msg) {
	const p = msg.payload || msg;
	for (const k of ["ttfb_seconds", "latency_seconds", "duration_seconds"]) {
		if (typeof p[k] === "number") return Math.round(p[k] * 1000);
	}
	for (const k of ["ms", "value", "latency", "duration"]) {
		if (typeof p[k] === "number") return Math.round(p[k]);
	}
	return null;
}

/**
 * @param {object}   handlers
 * @param {function} handlers.onBotSpeaking    (isSpeaking: boolean)
 * @param {function} handlers.onUserTranscript (text)
 * @param {function} handlers.onBotText        (text)
 * @param {function} handlers.onUserSpeaking   (interimText) - guest is mid-sentence
 * @param {function} handlers.onToolCall       (started: boolean, name) - agent looking something up
 * @param {function} handlers.onNodeTransition (msg)
 * @param {function} handlers.onLatency        ({type, ms})
 * @param {boolean}  handlers.debug            log each type's payload keys once
 */
export function startEventTap(handlers = {}) {
	const {
		onBotSpeaking = noop,
		onUserTranscript = noop,
		onUserSpeaking = noop,
		onToolCall = noop,
		onBotText = noop,
		onNodeTransition = noop,
		onLatency = noop,
		debug = false,
	} = handlers;

	stopEventTap();

	const seen = new Set();

	// Returns true when the message was consumed and should NOT reach the widget.
	const handle = (raw) => {
		let msg;
		try {
			msg = typeof raw === "string" ? JSON.parse(raw) : raw;
		} catch {
			return false;
		}
		const type = msg && (msg.type || msg.event);
		if (!type) return false;

		if (debug && !seen.has(type)) {
			seen.add(type);
			console.log(`[tap] ${type} payload keys:`, Object.keys(msg.payload || {}), msg.payload);
		}

		switch (type) {
			case "rtf-bot-started-speaking":
				onBotSpeaking(true);
				break;
			case "rtf-bot-stopped-speaking":
				onBotSpeaking(false);
				break;
			case "rtf-user-transcription": {
				const p = msg.payload || {};
				// Interim results flicker as the guest speaks; only a final one
				// means they actually said something. The interim ones are still
				// worth something: they are the only signal that the guest is
				// mid-sentence right now, which is what lets the face show that
				// she is hearing them rather than just waiting.
				if (p.final === false) onUserSpeaking(textOf(msg));
				else onUserTranscript(textOf(msg));
				break;
			}
			case "rtf-function-call-start":
			case "rtf-function-call-end": {
				const p = msg.payload || {};
				onToolCall(type === "rtf-function-call-start", p.function_name || p.name || "");
				break;
			}
			case "rtf-bot-text":
				onBotText(textOf(msg));
				break;
			case "rtf-node-transition":
				console.log(`[tap] node -> ${msg.node_name || msg.node_id}`);
				onNodeTransition(msg);
				break;
			case "rtf-ttfb-metric":
			case "rtf-latency-measured": {
				const ms = millisOf(msg);
				if (ms != null) onLatency({ type, ms, model: (msg.payload || {}).model });
				break;
			}
			default:
				break;
		}

		return WIDGET_IGNORES.has(type);
	};

	const attach = () => {
		const state = window.DograhWidget && window.DograhWidget.getState && window.DograhWidget.getState();
		const ws = state && state.ws;
		if (!ws || ws === tapped) return false;

		const previous = ws.onmessage;
		ws.onmessage = function (evt) {
			let consumed = false;
			try {
				consumed = handle(evt.data);
			} catch (err) {
				console.error("[tap] handler threw:", err);
			}
			// Only messages the widget actually understands are forwarded. The rest
			// would just produce "Unknown message type" noise.
			if (!consumed && previous) previous.call(this, evt);
		};
		tapped = ws;
		console.log("[tap] listening to the Dograh event stream");
		return true;
	};

	// The socket only exists once a call is starting, and it is replaced on each
	// reconnect, so keep checking rather than attaching once.
	pollTimer = setInterval(attach, 500);
	attach();
}

export function stopEventTap() {
	if (pollTimer) clearInterval(pollTimer);
	pollTimer = null;
	tapped = null;
}

const eventTap = { startEventTap, stopEventTap };

export default eventTap;
